import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2, CalendarDays, Users, HardHat, CheckCircle2,
  AlertTriangle, Info, Activity, ListChecks, CircleDot, RotateCw, Gauge as GaugeIcon,
} from 'lucide-react';
import api from '../../lib/api';
import { fmtDate } from '../../lib/format';
import { cn } from '../../lib/utils';

// Tone → design-token colour (kept token-driven for light/dark parity).
const TONE = {
  success: 'success', warning: 'warning', danger: 'danger',
  info: 'brand', muted: 'muted-foreground', neutral: 'brand',
};
const toneVar = (t) => TONE[t] || 'brand';

const ATTENTION_ICON = {
  no_pm: Users, no_engineers: Users, none_certified: HardHat,
  to_certify: ListChecks, last_certified: CheckCircle2,
  idling: AlertTriangle, under_producing: Activity,
};

// KPI tile with an optional progress bar (percentages only — no money).
const KpiTile = ({ label, value, suffix, tone, pct, testid, hint }) => (
  <div className="erak-card p-4" data-testid={testid}>
    <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className={cn('mt-0.5 flex items-baseline gap-1 font-num text-2xl font-bold tabular-nums', tone && `text-[hsl(var(--${tone}))]`)}>
      {value}{suffix && <span className="text-sm font-semibold text-muted-foreground">{suffix}</span>}
    </div>
    {pct != null && (
      <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn('h-full rounded-full transition-[width] duration-300', tone ? `bg-[hsl(var(--${tone}))]` : 'bg-[hsl(var(--brand))]')}
          style={{ width: `${Math.min(Math.max(pct, 0), 100)}%` }} />
      </div>
    )}
    {hint && <div className="mt-1.5 text-[11px] text-muted-foreground">{hint}</div>}
  </div>
);

// A single labelled progress bar (used in the productivity panel & categories).
const Bar = ({ label, value, tone = 'brand', testid }) => (
  <div data-testid={testid}>
    <div className="mb-1 flex items-center justify-between text-xs">
      <span className="font-medium text-muted-foreground">{label}</span>
      <span className="font-num tabular-nums">{value == null ? '—' : `${value}%`}</span>
    </div>
    <div className="h-2.5 w-full overflow-hidden rounded-full bg-muted">
      <div className={cn('h-full rounded-full transition-[width] duration-300', `bg-[hsl(var(--${tone}))]`)}
        style={{ width: `${Math.min(Math.max(value || 0, 0), 100)}%` }} />
    </div>
  </div>
);

export function ProjectOverview({ projectId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false); setData(null);
    try {
      const { data: d } = await api.get(`/budget/projects/${projectId}/overview`);
      setData(d);
    } catch {
      setError(true);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <div className="erak-card flex flex-col items-center gap-3 p-10 text-center" data-testid="overview-error">
        <AlertTriangle size={28} className="text-[hsl(var(--danger))]" />
        <div className="text-sm text-muted-foreground">Could not load the project overview.</div>
        <button onClick={load} data-testid="overview-retry"
          className="inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium hover:bg-muted">
          <RotateCw size={14} />Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-4" data-testid="overview-loading">
        <div className="h-28 animate-pulse rounded-2xl bg-muted/40" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-muted/40" />)}
        </div>
        <div className="h-40 animate-pulse rounded-2xl bg-muted/40" />
      </div>
    );
  }

  const { header, health, kpis, productivity, categories = [], needsAttention = [] } = data;
  const ht = toneVar(health?.tone);
  const daysLeft = kpis?.daysLeft;
  const overdue = daysLeft != null && daysLeft < 0;
  const lc = kpis?.linesCertified || { done: 0, total: 0 };
  const lcPct = lc.total > 0 ? Math.round((lc.done / lc.total) * 100) : 0;

  return (
    <div className="space-y-5" data-testid="project-overview">
      {/* Header + health */}
      <div className="erak-card p-5" data-testid="overview-header">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-num text-xs text-[hsl(var(--copper))]">
              <Building2 size={13} />{header?.code}
              <span data-testid="overview-health"
                className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium',
                  `bg-[hsl(var(--${ht})/0.12)] text-[hsl(var(--${ht}))]`)}>
                <CircleDot size={11} />{health?.label}
              </span>
            </div>
            <h2 className="mt-0.5 font-display text-xl font-bold md:text-2xl">{header?.name}</h2>
            <p className="text-sm text-muted-foreground">{header?.client || 'Client not set'}</p>
          </div>
          <div className="flex flex-col items-start gap-1.5 text-xs sm:items-end">
            <span className="flex items-center gap-1.5 text-muted-foreground" data-testid="overview-duration">
              <CalendarDays size={13} />
              {header?.startDate ? fmtDate(header.startDate) : '—'} → {header?.endDate ? fmtDate(header.endDate) : '—'}
            </span>
            <span className="flex items-center gap-1.5">
              <Users size={13} className="text-muted-foreground" />
              <span className="font-medium">PM:</span>
              <span className={cn(!header?.projectManagerName && 'text-[hsl(var(--warning))]')}>
                {header?.projectManagerName || 'Unassigned'}
              </span>
            </span>
          </div>
        </div>
      </div>

      {/* Execution KPI tiles (percentages / counts / dates only) */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-testid="overview-kpis">
        <KpiTile testid="kpi-physical" label="Physical Execution" value={kpis?.physicalExecutionPct ?? 0} suffix="%"
          tone={ht} pct={kpis?.physicalExecutionPct ?? 0} hint="Value-weighted" />
        <KpiTile testid="kpi-time" label="Time Elapsed" value={kpis?.timeElapsedPct ?? '—'} suffix={kpis?.timeElapsedPct == null ? '' : '%'}
          pct={kpis?.timeElapsedPct} hint={kpis?.timeElapsedPct == null ? 'Timeline not set' : 'Of scheduled duration'} />
        <KpiTile testid="kpi-days-left" label="Days Left" value={daysLeft == null ? '—' : Math.abs(daysLeft)}
          suffix={daysLeft == null ? '' : (overdue ? ' overdue' : ' days')} tone={overdue ? 'danger' : undefined}
          hint={header?.endDate ? `Ends ${fmtDate(header.endDate)}` : 'End date not set'} />
        <KpiTile testid="kpi-lines-certified" label="Lines Certified" value={`${lc.done} / ${lc.total}`}
          pct={lcPct} hint={`${lcPct}% of lines certified`} />
      </div>

      {/* Productivity panel — execution vs time only */}
      <div className="erak-card p-5" data-testid="overview-productivity">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <GaugeIcon size={16} className="text-[hsl(var(--brand))]" />
            <h3 className="font-display text-base font-semibold">Productivity</h3>
          </div>
          <span data-testid="overview-productivity-read"
            className={cn('inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
              `bg-[hsl(var(--${toneVar(productivity?.tone)})/0.12)] text-[hsl(var(--${toneVar(productivity?.tone)}))]`)}>
            <Activity size={13} />{productivity?.read}
          </span>
        </div>
        <div className="space-y-3">
          <Bar testid="prod-bar-execution" label="Physical execution" value={productivity?.physicalExecutionPct} tone={ht} />
          <Bar testid="prod-bar-time" label="Time elapsed" value={productivity?.timeElapsedPct} tone="muted-foreground" />
        </div>
      </div>

      {/* Execution by category — % bars only (no AED) */}
      {categories.length > 0 && (
        <div className="erak-card p-5" data-testid="overview-categories">
          <h3 className="mb-3 font-display text-base font-semibold">Execution by category</h3>
          <div className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {categories.map((c) => (
              <Bar key={c.category} testid={`overview-cat-${c.category}`}
                label={c.category} value={c.executedPct} tone="brand" />
            ))}
          </div>
        </div>
      )}

      {/* Needs attention — execution flags only */}
      <div className="erak-card p-5" data-testid="overview-attention">
        <h3 className="mb-3 flex items-center gap-2 font-display text-base font-semibold">
          <AlertTriangle size={16} className="text-[hsl(var(--warning))]" />Needs attention
        </h3>
        {needsAttention.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="overview-attention-clear">
            <CheckCircle2 size={15} className="text-[hsl(var(--success))]" />All clear — nothing needs attention.
          </div>
        ) : (
          <ul className="space-y-2">
            {needsAttention.map((a) => {
              const Icon = ATTENTION_ICON[a.key] || Info;
              const t = toneVar(a.tone);
              return (
                <li key={a.key} data-testid={`attention-${a.key}`}
                  className="flex items-center gap-2.5 rounded-lg border bg-muted/20 px-3 py-2 text-sm">
                  <span className={cn('grid h-6 w-6 shrink-0 place-items-center rounded-md',
                    `bg-[hsl(var(--${t})/0.12)] text-[hsl(var(--${t}))]`)}>
                    <Icon size={13} />
                  </span>
                  <span>{a.label}{a.date ? <span className="text-muted-foreground"> · {fmtDate(a.date)}</span> : ''}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
